import { useEffect } from "react";
import { createPortal } from "react-dom";

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "danger" | "primary" | "success";
  isLoading?: boolean;
  loadingText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "danger",
  isLoading = false,
  loadingText,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        onCancel();
      }
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (isOpen) {
      // Blur any focused element to remove focus rings/highlights from background
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // Prevent interaction with background content
      const mainContent = document.getElementById("main-content");
      if (mainContent) {
        mainContent.style.pointerEvents = "none";
      }
      return () => {
        if (mainContent) {
          mainContent.style.pointerEvents = "";
        }
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const confirmBtnClass =
    confirmVariant === "danger"
      ? "bg-[#eb1515] text-white hover:bg-red-700"
      : confirmVariant === "success"
        ? "bg-[#137333] text-white hover:bg-[#0d5c2a]"
        : "bg-[#2d89fe] text-white hover:bg-blue-600";

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm mx-4 rounded-lg bg-white p-6 shadow-lg text-center">
        <h3 className="text-base font-semibold text-[rgba(25,25,25,0.9)]">{title}</h3>
        <div className="mt-2 text-sm text-[rgba(25,25,25,0.7)]">{message}</div>
        <div className="mt-5 flex justify-center gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="h-9 px-4 rounded-md border border-[#efe9e4] bg-white text-sm font-medium text-[rgba(25,25,25,0.8)] hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`h-9 px-4 rounded-md text-sm font-medium disabled:opacity-50 cursor-pointer ${confirmBtnClass}`}
          >
            {isLoading && loadingText ? loadingText : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
